import Loki from "lokijs";
import {MappingComponent} from "./MappingComponent";
import {isObject, isString} from "@/utils/TypeUtil";


/**
 * エンティティのモデル型
 */
export type EntityModel = Record<string, any>;

/**
 * 差分を表現します
 */
type DeltaRow = Record<string, any>;

/**
 * 追加・更新・削除の差分を表現します
 */
interface Delta {
    /** 追加差分 */
    add: DeltaRow[];

    /** 更新差分 */
    update: DeltaRow[];

    /** 削除差分 */
    remove: DeltaRow[];
}

/** 差分のマッピングを表現します */
type DeltaMapper<E extends EntityModel> =
    // 配列形式
    (
        // ["キー名", "キー名", ...] 形式
        // 送信するキー名と元のキー名が同一の場合に有効
        keyof E |
        // [{name: "送信するキー名", source: "元のキー名"}, ...] 形式
        {name: string; source: keyof E} |
        // [{name: "送信するキー名", source: row => {...}}, ...] 形式
        // 対象行のデータから加工されたデータを、指定されたキー名で送信する場合に有効
        {name: string; source: (row: E) => any}
    )[] |
    // クロージャ形式
    ((row: E) => DeltaRow);

/**
 * 追加・更新・削除の差分のマッピングを表現します
 */
interface DeltaMappers<E extends EntityModel> {
    /** 追加差分のマッピング */
    add?: DeltaMapper<E>;

    /** 更新差分のマッピング */
    update?: DeltaMapper<E>;

    /** 削除差分のマッピング */
    remove?: DeltaMapper<E>;
}

/**
 * Collection に Mixin して Entity にする為のクラス
 */
class CollectionMixin<E extends EntityModel> {
    /** エンティティーの主キー */
    primaryKey: (keyof E)[];

    /** エンティティーのユニーク制約 */
    uniqueKeys: (keyof E)[][];

    /**
     * コンストラクタ
     *
     * @param primaryKey エンティティーの主キー
     * @param uniqueKeys エンティティーのユニーク制約
     */
    constructor(primaryKey: (keyof E)[], uniqueKeys: (keyof E)[][]) {
        this.primaryKey = primaryKey;
        this.uniqueKeys = uniqueKeys;
    }

    /**
     * 指定されたキーの値からユニークな文字列を生成します
     *
     * @param change 変更
     */
    private buildKey(change: CollectionChange): string {
        if(this.primaryKey.length === 1) {
            return JSON.stringify(change.obj[this.primaryKey[0]]);
        } else {
            const values = {} as { [key in keyof E]: any };
            for (let k of this.primaryKey) {
                values[k] = change.obj[k];
            }
            return JSON.stringify(values);
        }
    }

    /**
     * 最小化した変更を取得します。
     * 例えば、データが作られ、更新され、削除されたという事は何もしていないのと同じに扱います。
     */
    public getDelta(this: Entity<any>, mapping?: DeltaMappers<E>): Delta {
        const changes = this.getChanges()
        const map = new Map<string, CollectionChange>();
        for(let nextChange of changes) {
            const key = this.buildKey(nextChange);
            if(map.has(key)) {
                const prevChange = map.get(key);
                switch(`${prevChange?.operation}-${nextChange.operation}`) {
                    case 'I-U':
                        map.set(key, {
                            name: nextChange.name,
                            operation: 'I',
                            obj: nextChange.obj
                        });
                        break;
                    case 'I-R':
                    case 'R-I':
                        map.delete(key);
                        break;
                    case 'U-U':
                        map.set(key, nextChange);
                        break;
                    case 'U-R':
                        map.set(key, {
                            name: nextChange.name,
                            operation: 'R',
                            obj: nextChange.obj
                        });
                        break;
                }
            } else {
                map.set(key, nextChange);
            }
        }
        const result = {add: [], update: [], remove: []} as Delta;
        for(let change of map.values()) {
            switch(change.operation) {
                case 'I':
                    result.add.push(this.mapDelta(change.obj, mapping?.add));
                    break;
                case 'U':
                    result.update.push(this.mapDelta(change.obj, mapping?.update));
                    break;
                case 'R':
                    result.remove.push(this.mapDelta(change.obj, mapping?.remove));
                    break;
            }
        }
        return result;
    }

    private mapDelta(row: E, mapping?: DeltaMapper<E>): DeltaRow {
        if(!mapping || !row) {
            return row;
        }

        const result = {} as Record<string, any>;
        if(typeof mapping === 'function') {
            return mapping(row);
        } else if(Array.isArray(mapping)) {
            for(let key of mapping) {
                if(isObject(key) && key.name && key.source) {
                    result[key.name] = typeof key.source === 'function' ? key.source(row) : row[key.source];
                } else if(typeof key === 'string') {
                    result[key as string] = row[key];
                } else {
                    throw new Error(`Illegal delta array format: ${key}`);
                }
            }
        } else {
            throw new Error(`Illegal delta format: ${mapping}`);
        }
        return result;
    }
}

/**
 * エンティティー型
 */
export type Entity<E extends EntityModel> = Collection<E> & CollectionMixin<E>;

/**
 * エンティティー名とエンティティー型のマッピング型
 */
export type EntityMap = {[key: string]: any};

/**
 * すべてのエンティティー型
 */
export type Entities<EM extends EntityMap> = {[K in keyof EM]: Entity<EM[K]>};

/**
 * データのマッピング型
 */
export type Mappings<EM extends EntityMap> = {[K in keyof EM]?: (string | string[])};

/**
 * エンティティー制約のセット
 */
export interface EntityConstraint {
    /**
     * 主キー
     * 複合主キーの場合は + で列名を繋いで指定します。
     */
    primaryKey: string;

    /**
     * ユニークキー制約
     * 複合キー制約の場合は + で列名を繋いで指定します。
     * 複数のユニークキー制約を指定する場合は , で繋いで指定します。
     */
    uniqueKeys?: string;

    /**
     * インデックス
     * 複数のインデックスを指定する場合は , で繋いで指定します。
     */
    indices?: string;
}

/**
 * データーベース
 * いわばエンティティの集合です
 *
 * @param <EM> エンティティー名とエンティティー型のマッピング型
 */
export class Database<EM extends EntityMap> {
    /** Loki */
    private _loki: Loki;

    /** エンティティ */
    private _entities = {} as Entities<EM>;

    /**
     * コピーコンストラクタ
     *
     * @param loki Loki
     */
    constructor(loki: Loki);

    /**
     * コンストラクタ
     *
     * @param name データーベース名
     * @param initializer データーベース初期化関数
     */
    constructor(name: string, initializer?: (payload: {createEntity: Database<EM>['createEntity']}) => void);

    /**
     * コンストラクタ実装
     *
     * @param loki Loki or データーベース名
     * @param initializer データーベース初期化関数
     */
    constructor(loki: Loki | string, initializer?: (payload: {createEntity: Database<EM>['createEntity']}) => void) {
        if(typeof loki === 'string') {
            this._loki = new Loki(loki);
            if(typeof initializer === 'function') {
                initializer({createEntity: this.createEntity.bind(this)});
            }
        } else {
            this._loki = loki;
        }
    }

    /**
     * Entity を生成します
     *
     * @param name エンティティー名
     * @param constraint エンティティーの制約セット、もしくは主キー
     */
    private createEntity<K extends keyof EM>(name: K, constraint: EntityConstraint | string): Entity<EM[K]> {
        const _constraint = this.convertEntityConstraint(constraint);
        const base = new CollectionMixin<EM[K]>(_constraint.primaryKey, _constraint.uniqueKeys);
        const collection = this._loki.addCollection<EM[K]>(name as string, {
            indices: _constraint.indices,
            disableChangesApi: false,
            clone: false,
            // cloneMethod: 'shallow-assign',
        });

        const entity = Object.assign(collection, base) as Entity<EM[K]>;
        Object.getOwnPropertyNames(CollectionMixin.prototype)
            .filter(name => name !== 'constructor')
            .forEach(name => {
                // @ts-ignore
                entity[name] = CollectionMixin.prototype[name];
            })
        this._entities[name] = entity;

        // ユニークチェック
        const checkUnique = (op: 'insert' | 'update', obj: EM[K]) => {
            // PrimaryKey
            const query: Partial<EM[K]> = {};
            _constraint.primaryKey.forEach(k => query[k] = obj[k]);
            const existing = entity.findOne(query);
            if((op ==='insert' && existing) || (op === 'update' && existing && obj.$loki !== existing.$loki)) {
                throw new Error(`Duplicate primary key while ${op} at ${name}: ${JSON.stringify(query)}` );
            }

            // UniqueKeys
            _constraint.uniqueKeys.forEach(uniqueKey => {
                const query: Partial<EM[K]> = {};
                uniqueKey.forEach(k => query[k] = obj[k]);
                const existing = entity.findOne(query);
                if((op ==='insert' && existing) || (op === 'update' && existing && obj.$loki !== existing.$loki)) {
                    throw new Error(`Duplicate unique key while ${op} at ${name}: ${JSON.stringify(query)}` );
                }
            });
        };
        entity.on('pre-insert', o => checkUnique('insert', o));
        entity.on('pre-update', o => checkUnique('update', o));

        return entity;
    }

    /**
     * EntityConstraint を変換します
     *
     * @param constraint EntityConstraint
     * @private
     */
    private convertEntityConstraint<K extends keyof EM>(constraint: EntityConstraint | string): {
        /** 主キー */
        primaryKey: (keyof EM[K])[];

        /** ユニークキー制約 */
        uniqueKeys: (keyof EM[K])[][];

        /** インデックス */
        indices: (keyof EM[K])[];
    } {
        if(isString(constraint)) {
            return this.convertEntityConstraint({primaryKey: constraint});
        }

        function expandPlus(names?: string): string[] {
            if(names) {
                return names.split('+').map(s => s.trim());
            } else {
                return [];
            }
        }

        function expandComma(names?: string): string[] {
            if(names) {
                return names.split(',').map(s => s.trim());
            } else {
                return [];
            }
        }

        return {
            primaryKey: expandPlus(constraint.primaryKey) as (keyof EM[K])[],
            uniqueKeys: expandComma(constraint.uniqueKeys).map(i => expandPlus(i)) as (keyof EM[K])[][],
            indices: expandComma(constraint.indices) as (keyof EM[K])[],
        };
    }

    /**
     * データーベースのクローンを生成します
     */
    public clone(): Database<EM> {
        return new Database(this._loki.copy());
    }

    /**
     * すべてのデータを削除します
     */
    public clear(): void {
        for(let entity of Object.values(this._entities)) {
            entity.clear({removeIndices: true});
            entity.flushChanges();
        }
    }

    /**
     * すべての変更差分を消去します
     */
    public flushChanges(): void {
        for(let entity of Object.values(this._entities)) {
            entity.flushChanges();
        }
    }

    /**
     * 指定されたエンティティー名の Entity を取得します
     *
     * @param name エンティティー名
     */
    public entity<K extends keyof EM>(name: K): Entity<EM[K]> {
        return this._entities[name] as Entity<EM[K]>;
    }

    /**
     * すべての Entity を取得します
     */
    public get entities(): Entities<EM> {
        return Object.assign({}, this._entities);
    }



    /**
     *
     * @param mapping データの位置とエンティティーとのマッピング
     * キー: エンティティ名
     * 値: マッピング文字列、もしくは文字列の配列
     *
     * ・マッピング文字列フォーマット
     * [オブジェクトのキー]("."[オブジェクトのキー])...(">" [置き換えに使用するオブジェクトのキー]("+"))("*")("-")
     *
     * - オブジェクトのキー
     *   取り込みたいオブジェクトの属しているオブジェクトのキー
     *   キーとして "-" を指定した場合、どのエンティティーにもマッピングを行いません
     * - 置き換えに使用するオブジェクトのキー
     *   取り込んだ後、データを置き換える際に使用するキー
     * - "+"
     *   置き換えに使用するオブジェクトのキーが存在しない場合、任意のIDを生成する
     * - "*"
     *   主キーの重複を許可します
     * - "-"
     *   取り込んだ後、オブジェクトを元の位置から削除します
     * - "?"
     *   変更を記録しません
     *
     * ・データの例
     * {
     *     foo: [{
     *         id: 123
     *         name: "ABC"
     *         bar: [{
     *             id: 456
     *             name: "DEF"
     *         }, {
     *             id: 789
     *             name: "GHI"
     *         }]
     *     }]
     * }
     * ・フラット化されたエンティティの例
     * {
     *     foo: [{
     *         id: 123
     *         name: "ABC"
     *         bar: [456, 789]
     *     }]
     *     bar: [{
     *         id: 456
     *         name: "DEF"
     *     }, {
     *         id: 789
     *         name: "GHI"
     *     }]
     * }
     * ・この場合のマッピング
     * {
     *     foo: "foo"
     *     bar: "foo.bar > id"
     * }
     * @return コンパイルされた MappingComponent の配列
     */
    public compileMapping(mapping: Mappings<EM>): MappingComponent[] {
        return Object.entries(mapping)
            .map(([key, value]) => {
                return [key, Array.isArray(value) ? value : [value]]
            })
            .flatMap(([key, value]) => {
                if(key === '-') {
                    return (value as string[]).map(path => new MappingComponent(null, path));
                } else {
                    const entity = this.entity(key as (keyof EM));
                    if (!entity) {
                        throw new Error(`No such entity: ${key}`);
                    }
                    return (value as string[]).map(path => new MappingComponent(entity, path));
                }
            })
            .sort((a, b) => b.pathLength - a.pathLength);
    }

    /**
     * 階層的なデータをフラットにロードします
     *
     * @param data データ
     * @param mappingComponents コンパイル済みマッピングの配列
     */
    public load(data: any, mappingComponents: MappingComponent[]): void {
        // 再帰的に取り込み
        this.insertRecursively(data, [], mappingComponents);
    }

    /**
     * データを再帰的に取り込む
     *
     * @param data データ
     * @param path データのパス
     * @param mappingComponents MappingComponent の配列
     */
    private insertRecursively(data: any, path: string[], mappingComponents: MappingComponent[]): any {
        if(data === null || data === undefined) {
            return data;
        } else if(Array.isArray(data)) {
            for(let i=0, len=data.length; i<len; i++) {
                data[i] = this.insertRecursively(data[i], path, mappingComponents);
            }
        } else if(typeof data === 'object') {
            for(let [key, value] of Object.entries(data)) {
                path.push(key);
                data[key] = this.insertRecursively(value, path, mappingComponents);
                path.pop();
            }

            for(let mc of mappingComponents) {
                if(mc.pathEquals(path)) {
                    delete data.__typename;
                    delete data.$loki;
                    delete data.meta;
                    if(mc.isGenerateId && mc.replaceTo && data[mc.replaceTo] === undefined) {
                        data[mc.replaceTo] = Database.uuid();
                    }

                    // console.log(path.join('.'), data);
                    if(mc.mappingTo) {
                        const orgDisableChangesApi = mc.mappingTo.disableChangesApi;
                        try {
                            mc.mappingTo.disableChangesApi = mc.mappingTo.disableChangesApi || mc.isDisableChangesApi;
                            mc.mappingTo.insert(data);
                        } catch (e) {
                            if (!mc.isAllowDuplicate) {
                                console.warn(mc.mappingTo.name, e);
                            }
                        } finally {
                            mc.mappingTo.disableChangesApi = orgDisableChangesApi;
                        }
                    }

                    if(mc.isDelete) {
                        return undefined;
                    } else if(mc.replaceTo) {
                        return data[mc.replaceTo];
                    } else {
                        return data;
                    }
                }
            }
        }
        return data;
    }

    /**
     * UUID を生成します
     *
     * @return 生成された UUID
     */
    private static uuid(): string {
        let uuid = "";
        for (let i = 0; i < 32; i++) {
            let random = Math.random() * 16 | 0;

            if (i == 8 || i == 12 || i == 16 || i == 20) {
                uuid += "-";
            }
            uuid += (i == 12 ? 4 : (i == 16 ? (random & 3 | 8) : random)).toString(16);
        }
        return uuid;
    }
}
